import React, { useEffect, useState } from "react";
import { freight } from "../../lib/freightApi.js";
import { useCarrier } from "./CarrierApp.jsx";
import { Shell, Back, Title, Section, Card, Button, Field, Select, Note, Quiet, C, errText, plural } from "./ui.jsx";

/**
 * Admins and access (the schematic's board): who can sign in, what each person sees, who can change
 * rules, and who handles which drivers and loads. Admins only; the server refuses everyone else
 * (backend access.mjs), and it is the server that narrows "only their own".
 */

export const ROLE = { admin: "Admin", dispatcher: "Dispatcher", recruiter: "Recruiter" };
export const SEES = { everything: "Everything", all: "All drivers and loads", own: "Only their drivers and loads", hiring: "Hiring only" };
const visuallyHidden = { position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" };
const cell = { padding: "12px 16px 12px 0", borderBottom: `1px solid ${C.soft}`, textAlign: "left", verticalAlign: "top" };
const head = { textAlign: "left", fontSize: 14, fontWeight: 600, color: C.muted, padding: "12px 16px 10px 0", borderBottom: `1px solid ${C.line}` };

const nameOf = (p) => p.name || p.email;
const canInvite = (stage) => ["rehearsal", "live"].includes(stage);

/** What one invite came to, in plain words. ⛔ A TIMEOUT MAY HAVE GONE: never "did not go" (review). */
const INVITE_ERROR = {
  timed_out_may_have_sent: "the mail service did not answer in time, so it may have gone. Check with them before sending it again",
  email_not_configured: "email is not set up on our server yet. Ask MuleWorks",
  no_frontend_url: "our server is missing its sign-in address. Ask MuleWorks",
  not_yet: "invites go out once your account is in rehearsal",
};
export function inviteWords(invite, email) {
  if (!invite) return null;
  if (invite.sent) return `Their sign-in link went to ${email}.`;
  const why = INVITE_ERROR[invite.error] || invite.message || "it did not go out";
  return invite.error === "timed_out_may_have_sent" ? `The invite to ${email} may have gone: ${why}.` : `The invite to ${email} did not go: ${why}.`;
}
const inviteLine = (p, stage) => {
  if (p.inviteError) return p.inviteError === "timed_out_may_have_sent" ? "The last invite may have gone" : "The last invite did not send";
  if (p.invited) return null;
  return ["rehearsal", "live"].includes(stage) ? "Not invited yet" : "Gets a sign-in link at go-live";
};

/** One person, being edited. The owner's role and sign-in are ours to change (MuleWorks). */
export function EditPerson({ p, stage, onSave, onRemove, onInvite, onCancel, busy }) {
  const [f, setF] = useState({ name: p.name || "", phone: p.phone || "", role: p.role, sees: p.sees === "own" ? "own" : "all", canChangeRules: p.canChangeRules });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const [confirming, setConfirming] = useState(false);
  const idp = `person-${p.id}`;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "8px 0 16px" }}>
      <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
        <Field id={`${idp}-name`} label="Name" value={f.name} onChange={set("name")} autoComplete="name" />
        <Field id={`${idp}-phone`} label="Cell" type="tel" value={f.phone} onChange={set("phone")} autoComplete="tel" />
        {!p.owner && (
          <Select id={`${idp}-role`} label="Role" value={f.role} onChange={set("role")}
            options={[{ v: "admin", label: "Admin" }, { v: "dispatcher", label: "Dispatcher" }, { v: "recruiter", label: "Recruiter" }]} />
        )}
        {!p.owner && f.role === "dispatcher" && (
          <Select id={`${idp}-sees`} label="Sees" value={f.sees} onChange={set("sees")}
            options={[{ v: "all", label: SEES.all }, { v: "own", label: SEES.own }]} />
        )}
      </div>
      {!p.owner && (
        <label htmlFor={`${idp}-rules`} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 16 }}>
          <input id={`${idp}-rules`} type="checkbox" checked={f.canChangeRules} onChange={(e) => setF((x) => ({ ...x, canChangeRules: e.target.checked }))} style={{ width: 20, height: 20 }} />
          Can change rules
        </label>
      )}
      {p.owner && <Quiet>The owner is an admin and can always change rules. MuleWorks changes who the owner is.</Quiet>}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <Button disabled={busy} onClick={() => onSave(p.owner ? { name: f.name, phone: f.phone } : f)}>Save</Button>
        <Button kind="secondary" disabled={busy} onClick={onCancel}>Cancel</Button>
        {canInvite(stage) && (!p.invited || p.inviteError) && <Button kind="secondary" disabled={busy} onClick={onInvite}>{p.invited ? "Send the invite again" : "Send their invite"}</Button>}
        {!p.owner && !p.you && (confirming
          ? <Button kind="secondary" disabled={busy} onClick={onRemove} label={`Yes, remove ${nameOf(p)}`}>{`Yes, remove ${p.name || "them"}`}</Button>
          : <Button kind="secondary" disabled={busy} onClick={() => setConfirming(true)}>Remove</Button>)}
      </div>
      {confirming && <Quiet>{`${nameOf(p)} will no longer sign in, and their drivers and loads go back to not assigned.`}</Quiet>}
    </div>
  );
}

export function AddPerson({ onAdd, onCancel, busy, stage }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", role: "dispatcher", sees: "all", canChangeRules: false });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  return (
    <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Add a person</h2>
      <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        <Field id="add-name" label="Name" value={f.name} onChange={set("name")} autoComplete="name" />
        <Field id="add-email" label="Email" type="email" value={f.email} onChange={set("email")} autoComplete="email" />
        <Field id="add-phone" label="Cell" type="tel" value={f.phone} onChange={set("phone")} autoComplete="tel" />
        <Select id="add-role" label="Role" value={f.role} onChange={set("role")}
          options={[{ v: "dispatcher", label: "Dispatcher" }, { v: "recruiter", label: "Recruiter" }, { v: "admin", label: "Admin" }]} />
        {f.role === "dispatcher" && (
          <Select id="add-sees" label="Sees" value={f.sees} onChange={set("sees")} options={[{ v: "all", label: SEES.all }, { v: "own", label: SEES.own }]} />
        )}
      </div>
      <label htmlFor="add-rules" style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 16 }}>
        <input id="add-rules" type="checkbox" checked={f.canChangeRules} onChange={(e) => setF((x) => ({ ...x, canChangeRules: e.target.checked }))} style={{ width: 20, height: 20 }} />
        Can change rules
      </label>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <Button disabled={busy || !f.email.trim()} onClick={() => onAdd(f)}>{canInvite(stage) ? "Add and send their sign-in link" : "Add"}</Button>
        <Button kind="secondary" disabled={busy} onClick={onCancel}>Cancel</Button>
      </div>
    </Card>
  );
}

/** Pick exactly which drivers one person handles. */
export function ChooseDrivers({ person, board, onSave, onCancel, busy }) {
  const [picked, setPicked] = useState(() => new Set(board.drivers.filter((d) => d.dispatcherId === person.id).map((d) => d.id)));
  const byId = Object.fromEntries(board.people.map((p) => [p.id, p]));
  const toggle = (id) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "8px 0 16px" }}>
      {board.drivers.length === 0 ? <Quiet>No drivers on the roster yet.</Quiet> : (
        <fieldset style={{ border: 0, margin: 0, padding: 0, display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
          <legend style={visuallyHidden}>Drivers {nameOf(person)} handles</legend>
          {board.drivers.map((d) => {
            const other = d.dispatcherId && d.dispatcherId !== person.id ? byId[d.dispatcherId] : null;
            return (
              <label key={d.id} htmlFor={`drv-${person.id}-${d.id}`} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 16, minHeight: 36 }}>
                <input id={`drv-${person.id}-${d.id}`} type="checkbox" checked={picked.has(d.id)} onChange={() => toggle(d.id)} style={{ width: 20, height: 20 }} />
                <span>{d.name}{other ? <span style={{ color: C.muted }}> · {nameOf(other)} now</span> : null}</span>
              </label>
            );
          })}
        </fieldset>
      )}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <Button disabled={busy} onClick={() => onSave([...picked])}>Save</Button>
        <Button kind="secondary" disabled={busy} onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

/** Give each load nobody handles to someone. */
export function AssignLoads({ board, onAssign, onDone, busy }) {
  const loads = board.loads.filter((l) => l.handledBy == null);
  const handlers = board.people.filter((p) => p.role !== "recruiter");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "8px 0 16px" }}>
      {loads.length === 0 ? <Quiet>Every open load has someone.</Quiet> : loads.map((l) => (
        <div key={l.id} style={{ display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap" }}>
          <span style={{ fontSize: 16, fontWeight: 600, minWidth: 140, paddingBottom: 12 }}>{l.ref}{l.driverName ? <span style={{ fontWeight: 400, color: C.muted }}> · {l.driverName}</span> : null}</span>
          <Select id={`load-${l.id}`} label={`Who handles ${l.ref}`} hideLabel value="" onChange={(v) => v && onAssign(l.id, Number(v))}
            options={[{ v: "", label: "Pick someone" }, ...handlers.map((p) => ({ v: String(p.id), label: nameOf(p) }))]} />
        </div>
      ))}
      <div><Button kind="secondary" disabled={busy} onClick={onDone}>Done</Button></div>
    </div>
  );
}

export function AccessView({ company, stage, board, error, busy, msg, open, setOpen, act }) {
  const people = board?.people || [];
  const handlers = people.filter((p) => p.role !== "recruiter");
  return (
    <Shell company={company} stage={stage}>
      <Back to="/carrier/settings">Settings</Back>
      <Title sub="Who can sign in, what they see, and who can change rules.">Admins and access</Title>
      {error && <Quiet>{error}</Quiet>}
      {board && (
        <>
          {open === "add"
            ? <AddPerson busy={busy} stage={stage} onCancel={() => setOpen(null)} onAdd={(f) => act(() => freight.addPerson(f), (r) => (r?.invite
                ? `Added. ${inviteWords(r.invite, f.email.trim())}` : `Added. ${f.email.trim()} gets a sign-in link when you go live.`))} />
            : <div style={{ display: "flex" }}><Button onClick={() => setOpen("add")} style={{ minHeight: 48, fontSize: 16, padding: "0 22px" }}>Add a person</Button></div>}
          <Note msg={msg} />

          <Card style={{ padding: "8px 24px", overflowX: "auto" }}>
            <table style={{ width: "100%", minWidth: 620, borderCollapse: "collapse", fontSize: 16 }}>
              <thead>
                <tr>
                  <th scope="col" style={head}>Name</th>
                  <th scope="col" style={head}>Role</th>
                  <th scope="col" style={head}>Sees</th>
                  <th scope="col" style={head}>Can change rules</th>
                  <th scope="col" style={{ ...head, textAlign: "right", paddingRight: 0 }}><span style={visuallyHidden}>Edit</span></th>
                </tr>
              </thead>
              <tbody>
                {people.map((p) => (
                  <React.Fragment key={p.id}>
                    <tr style={{ background: open === `edit-${p.id}` ? C.accentWash : undefined }}>
                      <th scope="row" style={{ ...cell, fontWeight: 600 }}>
                        {nameOf(p)}{p.you ? " (you)" : ""}
                        {inviteLine(p, stage) && <span style={{ display: "block", fontSize: 14, fontWeight: 400, color: p.inviteError ? C.bad : C.muted }}>{inviteLine(p, stage)}</span>}
                      </th>
                      <td style={cell}>{ROLE[p.role] || p.role}{p.owner ? ", owner" : ""}</td>
                      <td style={cell}>{SEES[p.sees] || p.sees}</td>
                      <td style={cell}>{p.canChangeRules ? "Yes" : "No"}</td>
                      <td style={{ ...cell, padding: "4px 0", textAlign: "right" }}>
                        <Button kind="quiet" label={`Edit ${nameOf(p)}`} onClick={() => setOpen(open === `edit-${p.id}` ? null : `edit-${p.id}`)} style={{ padding: "0 8px" }}>Edit</Button>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </Card>
          {/* The editor sits below the table, not inside it, so on a phone it is not in a sideways scroller (review). */}
          {people.filter((p) => open === `edit-${p.id}`).map((p) => (
            <Card key={p.id} style={{ padding: 24, display: "flex", flexDirection: "column", gap: 8 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{`Edit ${nameOf(p)}`}</h2>
              <EditPerson p={p} stage={stage} busy={busy} onCancel={() => setOpen(null)}
                onSave={(f) => act(() => freight.updatePerson(p.id, f), "Saved.")}
                onInvite={() => act(() => freight.invitePerson(p.id), (r) => inviteWords(r?.invite, p.email) || "Nothing was sent.")}
                onRemove={() => act(() => freight.updatePerson(p.id, { active: false }), `${nameOf(p)} can no longer sign in.`)} />
            </Card>
          ))}

          <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
            <Section id="handles-title" title="Who handles which drivers and loads"
              sub="Anyone set to only their drivers and loads sees just these: in Dispatch, in search and in Recent. A load goes to its driver's dispatcher unless you assign it to someone else.">
              <div style={{ display: "flex", flexDirection: "column" }}>
                {handlers.map((p) => (
                  <div key={p.id} style={{ borderTop: `1px solid ${C.soft}` }}>
                    <div style={{ padding: "12px 0", display: "flex", alignItems: "center", gap: "6px 16px", flexWrap: "wrap" }}>
                      <span style={{ fontSize: 16, fontWeight: 600, minWidth: 200 }}>{nameOf(p)}</span>
                      <span style={{ fontSize: 16, color: C.muted, flex: "1 1 240px" }}>{p.drivers} {plural(p.drivers, "driver", "drivers")} · {p.openLoads} open {plural(p.openLoads, "load", "loads")}</span>
                      <Button kind="quiet" label={`Change the drivers ${nameOf(p)} handles`} onClick={() => setOpen(open === `drivers-${p.id}` ? null : `drivers-${p.id}`)}>Change</Button>
                    </div>
                    {open === `drivers-${p.id}` && <ChooseDrivers person={p} board={board} busy={busy} onCancel={() => setOpen(null)}
                      onSave={(ids) => act(() => freight.setPersonDrivers(p.id, ids), "Saved.")} />}
                  </div>
                ))}
                <div style={{ borderTop: `1px solid ${C.soft}` }}>
                  <div style={{ padding: "12px 0", display: "flex", alignItems: "center", gap: "6px 16px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: 16, fontWeight: 600, minWidth: 200 }}>Not assigned</span>
                    <span style={{ fontSize: 16, color: C.muted, flex: "1 1 240px" }}>
                      {board.notAssigned} {plural(board.notAssigned, "load", "loads")}{board.scoping && board.notAssigned ? " · admins see these in Needs you" : ""}
                    </span>
                    {board.notAssigned > 0 && <Button kind="quiet" onClick={() => setOpen(open === "assign" ? null : "assign")}>Assign</Button>}
                  </div>
                  {open === "assign" && <AssignLoads board={board} busy={busy} onDone={() => setOpen(null)}
                    onAssign={(loadId, who) => act(() => freight.setLoadDispatcher(loadId, who), "Assigned.", { keepOpen: true })} />}
                </div>
              </div>
            </Section>
          </Card>

          <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>What each role can do</h2>
            <dl style={{ margin: 0, display: "flex", flexDirection: "column", gap: 12 }}>
              {[["Admin", "Everything, including Settings, billing and adding people."],
                ["Dispatcher", "Dispatch: all of it, or only their own drivers and loads. Rules too, if you allow it."],
                ["Recruiter", "Hiring. Rules too, if you allow it."]].map(([t, d]) => (
                <div key={t} style={{ display: "flex", gap: "4px 16px", flexWrap: "wrap" }}>
                  <dt style={{ fontWeight: 600, minWidth: 120 }}>{t}</dt>
                  <dd style={{ margin: 0, color: C.muted, flex: "1 1 300px" }}>{d}</dd>
                </div>
              ))}
            </dl>
          </Card>
        </>
      )}
      {!board && !error && <Quiet>Loading…</Quiet>}
    </Shell>
  );
}

export default function Access() {
  const { carrier, me, reload } = useCarrier();
  const [board, setBoard] = useState(null);
  const [error, setError] = useState(me.admin ? null : "Only an admin at your company can open this.");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    if (!me.admin) return undefined;
    let alive = true;
    freight.people().then((b) => alive && setBoard(b)).catch((e) => alive && setError(errText(e)));
    return () => { alive = false; };
  }, [me.admin]);
  /** Run one change; the server answers with the board as it now stands. */
  const act = async (fn, ok, { keepOpen = false } = {}) => {
    setBusy(true); setMsg(null);
    try {
      const r = await fn();
      if (r?.people) setBoard(r);
      setMsg({ ok: true, text: typeof ok === "function" ? ok(r) : ok });
      if (!keepOpen) setOpen(null);
      // Your own row may have changed what you can open (an admin who made themselves a dispatcher).
      if (r?.people?.some((p) => p.you && (p.role !== "admin"))) reload();
    } catch (e) { setMsg({ ok: false, text: errText(e) }); }
    finally { setBusy(false); }
  };
  return <AccessView company={carrier.name} stage={carrier.stage} board={board} error={error} busy={busy} msg={msg} open={open} setOpen={setOpen} act={act} />;
}
